import React from "react"
import logo from "../../public/assets/images/ontario_logo.svg"
import { ProgramContainer } from "src/entities/formTemplateDataType";
import '../../public/assets/css/App.css';
import UserComponent from "../security/UserComponent";
import { UserProfile } from "../entities/CxpType";
import CustomPopup from "../runtimeComponent/subComponent/PopupComponent";
import { ReactComponent as AccountIcon } from "../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-account.svg";
import { headerMenu, MenuOptions, menuOptions, withSignOutLast } from "../entities/Constants";
import { cleanup, signin } from "../security/PublicSecure";
import TrackPageView from "../utilities/TrackPageView";
import { ReactComponent as ExitIcon } from "../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-exit.svg";
import { OntarioConfirmDialog } from "./OntarioConfirmDialog";
import SessionStorageService from "../services/SessionStorageService";
import SectionUtils from "../utilities/SectionUtils";
import { tr } from "date-fns/locale";
import axios from "axios";
import { pushSectionUrlToHistory } from "../utilities/URLUtils";
import { getEnvVar } from "../config/RuntimeConfig";

interface Props {
  programContainer: ProgramContainer;
  isEng: boolean;
  callback;
  profile?: UserProfile;
  callbackProgramContainer?;
  initialLandingURL?: string;
  // onAllowExit?: () => void;
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
}

interface State {
  isEng: boolean;
  isActive: boolean;
  menuOptions?: MenuOptions[]
  showExitDialog: boolean;
  exitDestination: string; // Track where we're navigating to
}

export class LabourHeaderComponent extends React.Component<Props, State> {
  HtmlToReactParser = require('html-to-react').Parser;

  constructor(props: Props) {
    super(props);
    const profileString = sessionStorage.getItem('userProfile');
    const profile: UserProfile | null = profileString ? JSON.parse(profileString) : null;

    if (profile && !this.props.programContainer.Profile)
      this.props.programContainer.Profile = profile;

    this.state = {
      isEng: this.props.isEng,
      isActive: false,
      menuOptions: this.getMenuOptions(),
      showExitDialog: false,
      exitDestination: "",
    }

  }

  getMenuOptions = (): MenuOptions[] => {
    const categoryId = this.props.programContainer.CategoryId?.toLowerCase();
    let options = headerMenu[categoryId];
    if (!options && this.props.programContainer.ForceApplicationHeaderMenu && this.props.programContainer.HeaderMenuConfig) {
      const configuredIds = this.props.programContainer.HeaderMenuConfig
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);
      options = menuOptions.filter(option => configuredIds.includes(option.id));
    }

    return withSignOutLast(options || []);
  }

  handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const ontarioUrl = "https://www.ontario.ca/page/government-ontario";

    const isDirty = SectionUtils.isFormDirty(this.props.programContainer);
    // Only show exit dialog if:
    // 1. EnableExitPopup is "Y" AND
    // 2. Form is dirty (has unsaved changes)
    if (this.props.programContainer.EnableExitPopup === "Y" && isDirty) {
      // In French mode, ignore English-only layout overrides so that
      // the built-in French dialog text is used instead.
      if (!this.props.isEng) {
        this.props.programContainer.ExitPopupTitle = "";
        this.props.programContainer.ExitPopupMessage = "";
        this.props.programContainer.ExitPopupStayButtonLabel = "";
        this.props.programContainer.ExitPopupExitButtonLabel = "";
      }
      this.setState({ showExitDialog: true, exitDestination: ontarioUrl });
    } else {
      // Navigate away directly if form is not dirty or exit popup is disabled
      window.location.href = ontarioUrl;

    }
  }

  handleAppNameClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // If this application requires Public Secure auth and there are no auth tokens,
    // redirect user to sign-in instead of navigating within the app.
    const authTokens = sessionStorage.getItem("authTokens");
    if (!authTokens && this.props.programContainer.AuthRequired === "ps") {
      signin(this.props.programContainer.CategoryId, this.props.isEng);
      return;
    }

    //Just OER need the Terms of Use check before accessing the app, 
    //if the user has not accepted the terms, do not allow them to proceed.
    if(authTokens && 
      this.props.programContainer.AuthRequired === "ps" && 
      (this.props.programContainer as any)?.Entity?.accepted === false) {
      return;
    }

    const isDirty = SectionUtils.isFormDirty(this.props.programContainer);
    if (this.props.programContainer.EnableExitPopup === "Y" && isDirty) {
      // In French mode, ignore English-only layout overrides so that
      // the built-in French dialog text is used instead.
      if (!this.props.isEng) {
        this.props.programContainer.ExitPopupTitle = "";
        this.props.programContainer.ExitPopupMessage = "";
        this.props.programContainer.ExitPopupStayButtonLabel = "";
        this.props.programContainer.ExitPopupExitButtonLabel = "";
      }
      this.setState({ showExitDialog: true, exitDestination: "" });
    } else {
      this.props.callbackStepIndex(2);
      pushSectionUrlToHistory(this.props.programContainer,this.props.programContainer.ListOfFormSections[2],2);
    }
  }

  handleConfirmExit = () => {
    const { exitDestination } = this.state;

    // First close the dialog, then perform the navigation/step change
    this.setState({ showExitDialog: false }, () => {
      if (exitDestination !== "") {
        window.location.href = exitDestination;
      } else {
        this.props.callbackStepIndex(2);
      }
    });
  }

  handleCancelExit = () => {
    this.setState({ showExitDialog: false, exitDestination: "" });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const languageChanged = prevProps.isEng !== this.props.isEng || prevState.isEng !== this.state.isEng;
    const menuChanged = prevState.menuOptions !== this.state.menuOptions;

    if (this.state.isActive && (languageChanged || menuChanged)) {
      this.setPopupWidth();
    }
  }

  getMenuLabel = (option: MenuOptions) => {
    const langKey: 'title-en' | 'title-fr' = this.props.isEng ? 'title-en' : 'title-fr';
    return option[langKey] ?? '';
  }

  getLongestMenuLabel = () => {
    const { menuOptions } = this.state;
    if (!menuOptions?.length) {
      return '';
    }

    return menuOptions.reduce((longest, option) => {
      const label = this.getMenuLabel(option);
      return label.length > longest.length ? label : longest;
    }, '');
  }

  calculateMenuWidth = () => {
    const longestLabel = this.getLongestMenuLabel();
    if (!longestLabel) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const fontSizePx = 18; // matches 1.125rem submenu text
    const fontDeclaration = `600 ${fontSizePx}px 'Open Sans','Helvetica Neue',Helvetica,Arial,sans-serif`;
    const fallbackCharWidth = fontSizePx * 0.6;
    const textWidth = (() => {
      if (!context) {
        return longestLabel.length * fallbackCharWidth;
      }
      context.font = fontDeclaration;
      return context.measureText(longestLabel).width;
    })();
    const ICON_AND_PADDING_WIDTH = 88; // icon width + flex gap + anchor padding + li padding

    return Math.ceil(textWidth + ICON_AND_PADDING_WIDTH);
  }

  setPopupWidth = () => {
    const popup = document.getElementById('header-menu-popup');
    if (!popup) {
      return;
    }

    const targetWidth = this.calculateMenuWidth();
    if (!targetWidth) {
      return;
    }

    popup.style.minWidth = `${targetWidth}px`;
    popup.style.width = `${targetWidth}px`;
  }

  handleMenuItem = async (e) => {
    const menu = document.getElementById('header-menu-popup');
    if (!menu || !menu.contains(e.target as Node)) {
      this.setState({ isActive: false });
      return;
    }

    let app = this.props.programContainer.CategoryId.toLowerCase();

    const target = e.target as HTMLElement;
    const id =
      target.id ||
      target.closest?.('li[data-menu-id]')?.getAttribute('data-menu-id') ||
      (target.closest?.('a[id]') as HTMLAnchorElement | null)?.id;

    if (!id) {
      return;
    }

    const menuItem = this.state.menuOptions.find(option => option.id === id);
    if (menuItem) {
      e.preventDefault();
      let url = '';
        if (id === 'signout') {    
          const idToken = SessionStorageService.getPSIDToken();
          await this.revokeToken();
          const logoutUrl =
            `${getEnvVar('PUBLIC_SECURE_ISSUER')}/v1/logout` +
            `?id_token_hint=${encodeURIComponent(idToken)}` +
            `&post_logout_redirect_uri=${encodeURIComponent(getEnvVar('PUBLIC_SECURE_SIGNOUT_REDIRECT_URI'))}`;
        
        cleanup(app, this.props.programContainer);
        
        setTimeout(() => {
          window.location.href = logoutUrl;
        }, 200); // Small delay ensures clean redirect

        // .then(response => {
        //   // Successfully notified backend, proceed with logout
        //   url = `${getEnvVar('PUBLIC_SECURE_ISSUER')}/v1/logout?${new URLSearchParams({
        //     idToken_hint: idToken,
        //     post_logout_redirect_uri: `${getEnvVar('PUBLIC_SECURE_SIGNOUT_REDIRECT_URI')}`,
        //   })}`;
        // })
      } else {
        url = this.getMenuLinkUrl(menuItem);
        window.location.assign(url);
      }
      // 
    }
    // cleanup(app, this.props.programContainer);
    this.setState({ isActive: false });
  }

  revokeToken = async () => {
    const url = `${this.props.programContainer.APIBasePath}/api/auth/revoke`;
    const header = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SessionStorageService.getPSToken()}`
    }
    try {
      await axios.post(url, {}, { headers: header });
    } catch (error) {
      console.error('Error revoking token:', error);
    }
  }

  getMenuLinkUrl = (option: MenuOptions) => {
    let app = this.props.programContainer.CategoryId.toLowerCase();
    if (app === 'otrip') app = 'otris';

    const lang = this.props.isEng ? 'en' : 'fr';
    let url = option[`link-${lang}`];

    return url;
  }

  addFrToUrl = () => {
    const enUrlString = window.location.href
    sessionStorage.setItem('previousUrl', enUrlString);
    let frUrlString
    if (enUrlString.includes("/on-form/")) {
      frUrlString = enUrlString.replace("/on-form/", "/on-form/fr/");
    } else {
      frUrlString = enUrlString + "/fr";
    }

    window.history.pushState({}, '', frUrlString);
    sessionStorage.setItem("languageChange", "true");
    TrackPageView(this.props.programContainer)
  }

  removeFrToUrl = () => {
    const url = window.location.href;
    sessionStorage.setItem('previousUrl', url);
    sessionStorage.setItem("languageChange", "true");
    if (url.includes("/fr")) {
      const newUrl = url.replace("/fr", "");
      window.history.pushState(null, "", newUrl);
    }

    TrackPageView(this.props.programContainer);
  }

  generateHeaderString = () => {
    const hasPSAuthToken = !!SessionStorageService.getPSToken();
    
    return (
      <div className='documentation-only--application'>
        <div className='ontario-header__container'>
          <header className='ontario-service-header ontario-header' id='ontario-header'>
            <div className='ontario-row'>
              <div className='ontario-columns ontario-small-6 ontario-application-header__logo'>
                <a href='https://www.ontario.ca/page/government-ontario' onClick={(e) => this.handleLogoClick(e)}>
                  <img id="asap-logo" src={logo} alt="Government of Ontario" />
                </a>
              </div>
              <div style={{ display: this.props.programContainer.EnglishOnly ? "none" : "block" }} className='ontario-columns ontario-small-6 ontario-application-header__lang-toggle'>
                <button lang={this.state.isEng ? 'fr' : 'en'} className='ontario-header__language-toggler ontario-header-button ontario-header-button--without-outline'
                  onClick={() => {
                    if (this.state.isEng) {
                      localStorage.setItem('cxp-i18n', 'fr');
                      this.addFrToUrl();
                    } else {
                      localStorage.setItem('cxp-i18n', 'en');
                      this.removeFrToUrl();
                    }
                    this.setState({ isEng: !this.props.isEng }, () => this.props.callback(!this.props.isEng))
                  }}
                >
                  {this.state.isEng ? 'Français' : 'English'}
                </button>
              </div>
            </div>
          </header>

          <section className='ontario-service-subheader'>
            <div className='subheader'>
              <div className='ontario-service-subheader__heading-container'>
                <p className='ontario-application-subheader__heading' style={{ whiteSpace: 'pre-wrap' }}>
                  <a
                    // className={SessionStorageService.getPSIDToken() ? "" : "not-clickable"}
                    href="#"
                    onClick={(e) => this.handleAppNameClick(e)}
                  >
                    {this.props.isEng ? this.props.programContainer.Name : this.props.programContainer.NameFRA}
                  </a>
                </p>
                {this.props.programContainer.AuthRequired == "ad" && (
                  <div className='ontario-service-subheader__heading'>
                    <a className="not-clickable"><UserComponent isEng={this.props.isEng} categoryId={this.props.programContainer.CategoryId} scopes={this.props.programContainer.Scopes} callback={() => this.props.callback()} /></a>
                  </div>
                )}
              </div>

              {(hasPSAuthToken && this.props.programContainer.Profile && this.props.programContainer.Profile?.userId || this.props.programContainer.ForceApplicationHeaderMenu) && this.state.menuOptions?.length && (
                <div className="header-submenu">
                  <div role="navigation" aria-label="Main">
                    <button
                      className="header__menu-toggler header-button header-button--with-outline submenu_button withborder ontario-header__menu-toggler ontario-header-button ontario-header-button--with-outline"
                      id="ontario-header-menu-toggler"
                      aria-controls="header-menu-popup"
                      aria-expanded={this.state.isActive}
                      aria-label={this.state.isActive ? 'Hide navigation menu' : 'Show navigation menu'}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.setState((prev) => ({ isActive: !prev.isActive }), () => {
                          if (this.state.isActive) {
                            this.setPopupWidth();
                          }
                        });
                      }}
                    >
                      <svg className="ontario-icon" focusable="false" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" aria-hidden='true'>
                        <use xlinkHref={
                          this.state.isActive
                            ? '#ontario-icon-close'
                            : '#ontario-icon-menu'
                        }
                          id="ontario-header-menu-icon" />
                      </svg>
                      <span>Menu</span>
                    </button>
                    {this.state.isActive && (
                      <CustomPopup
                        id="header-menu-popup"
                        onClickOutside={() => {
                          this.setState({ isActive: false });
                        }}
                        isVisible={this.state.isActive}
                        handleCustomEvent={this.handleMenuItem}
                      >
                        <ul id="header-menu-popup" className="submenu-items">
                          {this.state.menuOptions?.map(option => (
                            <li key={option.id} data-menu-id={option.id}>
                              {option.id === 'signout' && <ExitIcon
                                width={24}
                                height={24}
                                aria-hidden="true" focusable="false"
                                role="img"
                                style={{ maxWidth: '10%' }}
                                aria-label={this.props.isEng ? option["title-en"] : option["title-fr"]}
                              />
                              }
                              {option.id === 'myAccount' && <AccountIcon
                                width={24} height={24}
                                alt={this.props.isEng ? 'account' : 'Compte'}
                                role='img'
                                fill="#000"
                              />}
                              {!['myAccount', 'signout'].includes(option.id) && <img src={option.icon}
                                width={24} height={24} style={{ maxWidth: '10%' }}
                                alt=""
                              />}
                              <a id={option.id} href="#">
                                {this.props.isEng ? option["title-en"] : option["title-fr"]}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </CustomPopup>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
          </div>
          </div>
      )
  }

  render() {
    return (
      <>
        <div style={{ marginBottom: '50px' }}>
          {this.generateHeaderString()}
        </div>
        <OntarioConfirmDialog
          visible={this.state.showExitDialog}
          title={this.props.programContainer.ExitPopupTitle || (this.props.isEng ? "Are you sure you want to exit?" : "Êtes-vous sûr de vouloir quitter?")}
          message={this.props.programContainer.ExitPopupMessage || (this.props.isEng ? "If you leave this page your progress will not be saved." : "Si vous quittez cette page, votre progression ne sera pas sauvegardée.")}
          cancelLabel={this.props.programContainer.ExitPopupStayButtonLabel || (this.props.isEng ? "Stay on this page" : "Rester sur cette page")}
          confirmLabel={this.props.programContainer.ExitPopupExitButtonLabel || (this.props.isEng ? "Exit without saving" : "Quitter sans sauvegarder")}
          onCancel={this.handleCancelExit}
          onConfirm={this.handleConfirmExit}
        />
      </>
    )
  }
}
